import { Color3, MeshBuilder, StandardMaterial, type Mesh, type Scene } from "@babylonjs/core";
import { HeroId, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { bell, click, cymbal, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero, ProjectileFrame, ZoneFrame } from "../types.ts";

const V = { weather: 0, boost: 1, storming: 2 } as const;
const PK = { Wind: pk(HeroId.Brainstorm, 0) } as const;
const ZK = { Hurricane: zk(HeroId.Brainstorm, 0) } as const;
const FX = { Wind: fxk(HeroId.Brainstorm, 0), Lightning: fxk(HeroId.Brainstorm, 1), Hurricane: fxk(HeroId.Brainstorm, 2) } as const;

const theme = {
  bpm: 104,
  octave: 1,
  voice: bell,
  notes: [
    [0, 0.5],
    [7, 0.5],
    [11, 0.5],
    [14, 1],
    [12, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const windBlade: Cue = trim(15, (o) => {
  whoosh(o, 0.5, 900, 4600, 0.22, 0, 1.5);
  tone(o, { f: 820, f1: 1700, glide: 0.1, gain: 0.08, d: 0.16 });
  click(o, 0.18, 5200, 0.003);
  return 0.34;
});
const boltFootnote: Cue = trim(10.5, (o) => {
  click(o, 0.45, 5500, 0.003);
  fm(o, { f: 1200, ratio: 2.01, index: 5, index1: 0.5, gain: 0.13, d: 0.18 });
  noise(o, { color: "crackle", type: "highpass", f: 3500, gain: 0.22, d: 0.16 });
  return 0.28;
});
const forecastFlip: Cue = trim(10, (o) => {
  whoosh(o, 0.35, 500, 2200, 0.32);
  [0, 7, 11].forEach((s, i) => bell(later(o, i * 0.05), 293.66 * 2 ** ((s + 12) / 12), 0.16, 0.45));
  return 0.6;
});
const peerReview: Cue = trim(7.7, (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 1300, f1: 3600, q: 1.4, gain: 0.5, a: 0.05, d: 0.32 });
  fm(o, { f: 260, ratio: 3, index: 4, index1: 0.4, gain: 0.15, d: 0.35 });
  cymbal(o, 0.22, 0.7);
  return 0.75;
});
const eyeExam: Cue = trim(-3.8, (o) => {
  thump(o, 0.8, 88, 32, 0.4);
  noise(o, { color: "brown", type: "lowpass", f: 1100, gain: 0.5, a: 0.12, d: 0.6 });
  whoosh(o, 0.65, 300, 3000, 0.6);
  return 1.0;
});
const stormReload: Cue = trim(10, (o) => {
  whoosh(o, 0.14, 700, 1800, 0.2);
  bell(later(o, 0.18), 293.66 * 2, 0.12, 0.35);
  click(later(o, 0.62), 0.16, 2800, 0.005);
  return 1.1;
});

type Gear = {
  halo: Mesh;
  cloud: Mesh;
  cape: Mesh;
  auraA: Mesh;
  auraB: Mesh;
  handL: Mesh;
  handR: Mesh;
  hair: Mesh[];
};

function mat(scene: Scene, color: string, glow = false, alpha = 1) {
  const m = new StandardMaterial(`mat-${color}`, scene);
  m.diffuseColor = Color3.FromHexString(color);
  if (glow) m.emissiveColor = Color3.FromHexString(color);
  m.alpha = alpha;
  return m;
}

const hero: ClientHero<Gear> = {
  id: HeroId.Brainstorm,
  bulk: 1.02,
  shape: { head: 1.08, torso: 0.98, arms: 1, legs: 1.08 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, toon } = g;
    const halo = add(MeshBuilder.CreateTorus("storm-headpiece", { diameter: 0.68, thickness: 0.035, tessellation: 32 }, scene), rig.head, glow("#ffe56b"));
    halo.position.y = 0.34;
    const hair: Mesh[] = [];
    for (const side of [-1, 0, 1]) {
      const lock = add(MeshBuilder.CreateBox("white-hair-lock", { width: 0.16, height: 0.62, depth: 0.08 }, scene), rig.head, toon("#eef3ff"));
      lock.position.set(side * 0.17, -0.1, 0.22);
      lock.rotation.z = side * 0.18;
      hair.push(lock);
    }
    const cloud = add(MeshBuilder.CreateSphere("storm-cloud", { diameter: 0.44, segments: 12 }, scene), rig.torso, toon("#d8dff9"));
    cloud.position.set(0, 0.44, 0.34);
    cloud.scaling.set(1.7, 0.55, 0.9);
    const cape = add(MeshBuilder.CreateBox("storm-cape", { width: 1.0, height: 1.25, depth: 0.045 }, scene), rig.torso, toon("#eef3ff"));
    cape.position.set(0, -0.22, 0.42);
    cape.rotation.x = 0.14;
    const handL = add(MeshBuilder.CreateSphere("left-lightning-hand", { diameter: 0.18, segments: 10 }, scene), rig.handL, glow("#ffe56b", 0.8));
    const handR = add(MeshBuilder.CreateSphere("right-lightning-hand", { diameter: 0.18, segments: 10 }, scene), rig.handR, glow("#ffe56b", 0.8));
    const auraA = addWorld(MeshBuilder.CreateTorus("weather-aura-a", { diameter: 5, thickness: 0.028, tessellation: 64 }, scene), glow("#9fb6ff", 0.36));
    const auraB = addWorld(MeshBuilder.CreateTorus("weather-aura-b", { diameter: 3.7, thickness: 0.022, tessellation: 64 }, scene), glow("#ffe56b", 0.34));
    auraA.rotation.x = auraB.rotation.x = Math.PI / 2;
    auraA.setEnabled(false);
    auraB.setEnabled(false);
    return { halo, cloud, cape, auraA, auraB, handL, handR, hair };
  },
  animate(gear, f) {
    const thunder = f.v[V.weather] > 0.5;
    const boosted = f.v[V.boost] > 0;
    gear.halo.rotation.y += f.dt * (thunder ? 6 : 2.5);
    gear.halo.scaling.setAll(boosted ? 1.24 + Math.sin(f.t * 12) * 0.06 : 1);
    gear.cloud.rotation.y = f.t * (thunder ? 1.5 : 0.7);
    gear.cape.rotation.x = 0.12 + Math.sin(f.t * 3.5) * 0.05 + (f.moveKind === 8 ? 0.18 : 0);
    gear.handL.scaling.setAll(thunder || f.action === 2 ? 1.4 + Math.sin(f.t * 32) * 0.2 : 0.8);
    gear.handR.scaling.copyFrom(gear.handL.scaling);
    gear.auraA.setEnabled(true);
    gear.auraB.setEnabled(true);
    for (const [i, ring] of [gear.auraA, gear.auraB].entries()) {
      ring.position.set(f.pos.x, f.pos.y + 0.06 + i * 0.08, f.pos.z);
      ring.rotation.z = f.t * (thunder ? 1.6 + i : 0.55 + i * 0.2);
      ring.scaling.setAll((boosted ? 1.22 : 1) * (thunder ? 0.86 + i * 0.18 : 1.1 - i * 0.14));
    }
    gear.auraA.setEnabled(!thunder || boosted || f.v[V.storming] > 0);
    gear.auraB.setEnabled(thunder || boosted || f.v[V.storming] > 0);
    if (f.moveKind === 8 || f.v[V.storming] > 0) return "holding-both";
    if (f.action === 1 || f.action === 2) return "holding-both-shoot";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: hv(c, eid, V.weather) > 0.5 ? "Thunder aura" : "Tornado aura", value: 1, color: hv(c, eid, V.weather) > 0.5 ? "#ffe56b" : "#9fb6ff" },
      { label: "Boost", value: hv(c, eid, V.boost) / 480, color: "#ffe56b" },
    ];
  },
  projectiles: {
    [PK.Wind]: (scene, f) => {
      const blade = MeshBuilder.CreateBox("wind-blade", { width: 0.08, height: 0.42, depth: 0.9 }, scene);
      const ring = MeshBuilder.CreateTorus("wind-edge", { diameter: 0.46, thickness: 0.025, tessellation: 24 }, scene);
      blade.material = mat(scene, "#d8ecff", true);
      ring.material = mat(scene, "#9fb6ff", true);
      ring.parent = blade;
      ring.rotation.x = Math.PI / 2;
      blade.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: { blade, ring },
        update(g: { blade: Mesh; ring: Mesh }, frame: ProjectileFrame) {
          g.blade.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          g.blade.rotation.y = Math.atan2(frame.dir.x, frame.dir.z);
          g.ring.rotation.z += frame.dt * 18;
        },
        dispose() {
          blade.dispose();
          ring.dispose();
        },
      };
    },
  },
  zones: {
    [ZK.Hurricane]: (scene, f: ZoneFrame) => {
      const dome = MeshBuilder.CreateTorus("hurricane-eye", { diameter: f.radius * 2, thickness: 0.08, tessellation: 64 }, scene);
      const core = MeshBuilder.CreateCylinder("hurricane-column", { diameter: f.radius * 0.7, height: 3.2, tessellation: 32 }, scene);
      dome.material = mat(scene, "#9fb6ff", true, 0.45);
      core.material = mat(scene, "#d8ecff", true, 0.16);
      dome.position.set(f.pos.x, f.pos.y + 0.1, f.pos.z);
      dome.rotation.x = Math.PI / 2;
      core.position.set(f.pos.x, f.pos.y + 1.6, f.pos.z);
      return {
        gear: { dome, core },
        update(g: { dome: Mesh; core: Mesh }, frame: ZoneFrame) {
          g.dome.position.set(frame.pos.x, frame.pos.y + 0.1, frame.pos.z);
          g.core.position.set(frame.pos.x, frame.pos.y + 1.6, frame.pos.z);
          g.dome.rotation.z += frame.dt * 4;
          g.core.rotation.y += frame.dt * 5;
        },
        dispose() {
          dome.dispose();
          core.dispose();
        },
      };
    },
  },
  fx: {
    [FX.Lightning]: {
      tracer(c, from, to) {
        const bolt = MeshBuilder.CreateCylinder("storm-bolt", { diameter: 0.06, height: 1, tessellation: 8 }, c.scene);
        const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
        const len = Math.hypot(dx, dy, dz);
        bolt.position.set(from.x + dx * 0.5, from.y + dy * 0.5, from.z + dz * 0.5);
        bolt.scaling.set(1, len, 1);
        bolt.rotation.y = Math.atan2(dx, dz);
        bolt.rotation.x = Math.PI / 2 + Math.asin(dy / (len || 1));
        bolt.material = c.glow("#fff36a", 0.75);
        c.timed(bolt, 0.11, (t, m) => m.scaling.set(1 - t * 0.6, len, 1 - t * 0.6));
      },
    },
    [FX.Wind]: { keepGeneric: true },
    [FX.Hurricane]: { keepGeneric: true },
  },
  icons: {
    [Slot.Primary]: "M4 13c5-6 11-6 16 0M6 16c4-3 8-3 12 0",
    [Slot.Secondary]: "M13 3l-5 9h4l-2 9 7-11h-4z",
    [Slot.Ability1]: "M5 8c4-3 10-3 14 0M5 16c4 3 10 3 14 0",
    [Slot.Ability2]: "M12 4v16M4 12h16M7 7l10 10M17 7L7 17",
    [Slot.Ultimate]: "M12 3a9 9 0 109 9M8 12c2-3 6-3 8 0",
    passive: "M12 5c4 3 4 11 0 14-4-3-4-11 0-14z",
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:brainstorm:wind", cue: windBlade }),
    reload: () => stormReload,
    ability: {
      [Slot.Secondary]: boltFootnote,
      [Slot.Ability1]: forecastFlip,
      [Slot.Ability2]: peerReview,
      [Slot.Ultimate]: eyeExam,
    },
    theme,
    themeTrim: { heroic: 2.3, hostile: -6.5, tease: 5 },
    selfLift: ({ alive }) => (alive ? loops.levitate : undefined),
    flight: { [PK.Wind]: loops.flight("orb") },
    zoneLoops: { [ZK.Hurricane]: { build: loops.barrage, gain: 0.9, range: 42 } },
  },
};

export default hero;
